"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
  Users,
  Star,
  ArrowLeft,
  Search,
  MapPin,
  Building2,
  Heart,
  Quote,
  Loader2,
  Sparkles,
} from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import {
  ShimmerBar,
  EmptyState,
  ErrorState,
} from "@/components/site/StatePrimitives";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import {
  seoMeta,
  seoLinks,
  jsonLd,
  breadcrumbLd,
  itemListLd,
  organizationLd,
  softwareApplicationLd,
} from "@/lib/seo/seo";

const PAGE_TITLE = "مشتریان ترندی اسمارت — ۵٬۰۰۰+ برند ایرانی";
const PAGE_DESC =
  "از تیم‌های دو نفره تا هلدینگ‌های ۱۲۰ نفره؛ نگاهی به برندهایی که هر روز با ترندی اسمارت رشد می‌کنند، همراه با نظرات و تجربه‌ی واقعی آن‌ها.";

type Size = "all" | "sme" | "mid" | "ent";
type Region = "all" | "tehran" | "iran" | "intl";

type Customer = {
  id: string;
  name: string;
  initials: string;
  industry: string;
  city: string;
  size: Exclude<Size, "all">;
  region: Exclude<Region, "all">;
  members: string;
  color: string;
  since: string;
  vip?: boolean;
};

type Testimonial = {
  id: string;
  text: string;
  author: string;
  role: string;
  brand: string;
  initials: string;
  rating: number;
  color: string;
};

const CUSTOMERS: Customer[] = [
  {
    id: "1",
    name: "مدآی",
    initials: "م.آ",
    industry: "پوشاک",
    city: "تهران",
    size: "sme",
    region: "tehran",
    members: "۸ نفر",
    color: "from-primary-main to-primary-300",
    since: "۱۴۰۲",
  },
  {
    id: "2",
    name: "روناآرایش",
    initials: "ر.آ",
    industry: "زیبایی",
    city: "اصفهان",
    size: "sme",
    region: "iran",
    members: "۴ نفر",
    color: "from-secondary-main to-secondary-300",
    since: "۱۴۰۳",
    vip: true,
  },
  {
    id: "3",
    name: "کافه‌من",
    initials: "ک.م",
    industry: "کافه",
    city: "شیراز",
    size: "sme",
    region: "iran",
    members: "۳ نفر",
    color: "from-teal to-light-blue",
    since: "۱۴۰۳",
  },
  {
    id: "4",
    name: "پیکسل‌آپ",
    initials: "پ.آ",
    industry: "دیجیتال",
    city: "تهران",
    size: "mid",
    region: "tehran",
    members: "۲۲ نفر",
    color: "from-purple to-blue",
    since: "۱۴۰۲",
    vip: true,
  },
  {
    id: "5",
    name: "آکادمی هنر",
    initials: "آ.ه",
    industry: "آموزش",
    city: "مشهد",
    size: "mid",
    region: "iran",
    members: "۱۸ نفر",
    color: "from-pink to-secondary-main",
    since: "۱۴۰۳",
  },
  {
    id: "6",
    name: "فرش‌مارت",
    initials: "ف.م",
    industry: "خرده‌فروشی",
    city: "تبریز",
    size: "mid",
    region: "iran",
    members: "۱۶ نفر",
    color: "from-primary-main to-secondary-main",
    since: "۱۴۰۳",
  },
  {
    id: "7",
    name: "گروه آرا",
    initials: "گ.آ",
    industry: "هلدینگ",
    city: "تهران",
    size: "ent",
    region: "tehran",
    members: "۱۲۰+ نفر",
    color: "from-blue to-purple",
    since: "۱۴۰۲",
    vip: true,
  },
  {
    id: "8",
    name: "نارنج",
    initials: "ن",
    industry: "صادرات",
    city: "دبی",
    size: "ent",
    region: "intl",
    members: "۸۰ نفر",
    color: "from-secondary-main to-primary-main",
    since: "۱۴۰۳",
  },
  {
    id: "9",
    name: "بانوبامبو",
    initials: "ب.ب",
    industry: "دکوراسیون",
    city: "تهران",
    size: "sme",
    region: "tehran",
    members: "۶ نفر",
    color: "from-teal to-primary-main",
    since: "۱۴۰۳",
  },
  {
    id: "10",
    name: "سروشاپ",
    initials: "س.ش",
    industry: "ورزشی",
    city: "کرج",
    size: "mid",
    region: "iran",
    members: "۱۲ نفر",
    color: "from-light-blue to-purple",
    since: "۱۴۰۳",
  },
  {
    id: "11",
    name: "گالری نارین",
    initials: "گ.ن",
    industry: "گالری هنری",
    city: "اصفهان",
    size: "sme",
    region: "iran",
    members: "۵ نفر",
    color: "from-pink to-purple",
    since: "۱۴۰۴",
  },
  {
    id: "12",
    name: "Persian Crafts",
    initials: "P.C",
    industry: "صنایع‌دستی",
    city: "لندن",
    size: "mid",
    region: "intl",
    members: "۳۰ نفر",
    color: "from-secondary-main to-pink",
    since: "۱۴۰۳",
  },
];

const TESTIMONIALS: Testimonial[] = [
  {
    id: "t1",
    text: "از وقتی ترندی اسمارت اومد، تیم ما عملاً دو برابر شد بدون استخدام. ساده و واقعا فارسی.",
    author: "نگار صفری",
    role: "بنیان‌گذار",
    brand: "مدآی",
    initials: "ن.ص",
    rating: 5,
    color: "from-primary-main to-primary-300",
  },
  {
    id: "t2",
    text: "بهترین تجربه پشتیبانی که از یک ابزار ایرانی داشتم. هم محصول و هم آدم‌ها.",
    author: "آرمیتا حق‌گو",
    role: "مارکتینگ",
    brand: "روناآرایش",
    initials: "آ.ح",
    rating: 5,
    color: "from-secondary-main to-secondary-300",
  },
  {
    id: "t3",
    text: "Trendi واقعا مثل یک همکار جدید شده. یاد می‌گیره و هر روز بهتر جواب می‌ده.",
    author: "علیرضا نوری",
    role: "Ops",
    brand: "پیکسل‌آپ",
    initials: "ع.ن",
    rating: 5,
    color: "from-purple to-blue",
  },
  {
    id: "t4",
    text: "ROI ما در ماه دوم مثبت شد. این نقطه‌ای که هیچ ابزار دیگری بهمون نداده بود.",
    author: "محمد علوی",
    role: "CRM Lead",
    brand: "فرش‌مارت",
    initials: "م.ع",
    rating: 5,
    color: "from-primary-main to-secondary-main",
  },
];

type Scenario = "loaded" | "loading" | "empty" | "error" | "vip-only" | "ent-only" | "no-results";

const SIZES: { id: Size; label: string }[] = [
  { id: "all", label: "همه اندازه‌ها" },
  { id: "sme", label: "کوچک" },
  { id: "mid", label: "متوسط" },
  { id: "ent", label: "بزرگ" },
];
const REGIONS: { id: Region; label: string }[] = [
  { id: "all", label: "همه مناطق" },
  { id: "tehran", label: "تهران" },
  { id: "iran", label: "سایر شهرها" },
  { id: "intl", label: "بین‌الملل" },
];

export default function CustomersPage() {
  const [scenario, setScenario] = useState<Scenario>("loading");
  const [size, setSize] = useState<Size>("all");
  const [region, setRegion] = useState<Region>("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (scenario !== "loading") return;
    const t = setTimeout(() => setScenario("loaded"), 1100);
    return () => clearTimeout(t);
  }, [scenario]);

  const items = useMemo(() => {
    if (scenario === "empty" || scenario === "no-results") return [];
    let l = CUSTOMERS;
    if (scenario === "vip-only") l = l.filter((c) => c.vip);
    if (scenario === "ent-only") l = l.filter((c) => c.size === "ent");
    if (size !== "all") l = l.filter((c) => c.size === size);
    if (region !== "all") l = l.filter((c) => c.region === region);
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      l = l.filter((c) => c.name.toLowerCase().includes(t) || c.industry.includes(t));
    }
    return l;
  }, [scenario, size, region, q]);

  return (
    <SiteShell>
      <Hero />

      <section className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-border bg-card/60 p-3 backdrop-blur-sm sm:flex-row">
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="نام برند یا صنعت..."
              className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SIZES.map((s) => (
              <button
                key={s.id}
                onClick={() => setSize(s.id)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black ${
                  size === s.id
                    ? "border-primary-main bg-primary-main/15 text-primary-main"
                    : "border-border text-muted-foreground hover:border-primary-main/40"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {REGIONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setRegion(r.id)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-black ${
                  region === r.id
                    ? "border-secondary-main bg-secondary-main/15 text-secondary-main"
                    : "border-border text-muted-foreground hover:border-secondary-main/40"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-12 sm:px-6">
        {scenario === "loading" ? (
          <Skel />
        ) : scenario === "error" ? (
          <ErrorState onRetry={() => setScenario("loading")} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={scenario === "no-results" ? "نتیجه‌ای پیدا نشد" : "هنوز مشتری ثبت‌شده‌ای نیست"}
            desc={
              scenario === "no-results"
                ? "فیلترها را تغییر بده."
                : "به‌زودی اولین برندها اضافه می‌شوند."
            }
            cta={
              <button
                onClick={() => {
                  setSize("all");
                  setRegion("all");
                  setQ("");
                  setScenario("loaded");
                }}
                className="rounded-xl bg-primary-main px-5 py-2.5 text-[12.5px] font-black text-white hover:bg-primary-400"
              >
                پاکسازی فیلترها
              </button>
            }
          />
        ) : (
          <CustomersInfinite items={items} />
        )}
      </section>

      <Testimonials />
      <CTA />
    </SiteShell>
  );
}

function Hero() {
  return (
    <section className="mx-auto max-w-7xl px-5 pt-16 pb-10 sm:px-6 sm:pt-24">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center"
      >
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2">
          <Heart size={14} className="text-secondary-main" />
          <span className="text-[12px] font-bold text-muted-foreground">۵٬۰۰۰+ کسب‌وکار</span>
        </div>
        <h1 className="mx-auto mb-5 max-w-3xl text-4xl font-black leading-[1.15] tracking-tight sm:text-5xl lg:text-6xl">
          برندهایی که هر روز با ترندی اسمارت
          <span className="block brand-gradient-text">بزرگ‌تر می‌شوند</span>
        </h1>
        <p className="mx-auto max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
          از تیم‌های دو نفره تا هلدینگ‌های ۱۲۰ نفره — ما افتخار می‌کنیم در کنار همه‌شان هستیم.
        </p>
      </motion.div>
    </section>
  );
}

function CustomerCard({ c, index }: { c: Customer; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (index % 12) * 0.03 }}
      className="group relative overflow-hidden rounded-2xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-primary-main/40 hover:shadow-xl hover:shadow-primary-main/10"
    >
      {c.vip && (
        <div className="absolute left-2 top-2 rounded-full bg-secondary-main/15 px-2 py-0.5 text-[9px] font-black text-secondary-main">
          VIP
        </div>
      )}
      <div
        className={`mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${c.color} text-[14px] font-black text-white shadow-md`}
      >
        {c.initials}
      </div>
      <div className="text-[14px] font-black text-foreground">{c.name}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{c.industry}</div>
      <div className="mt-3 flex items-center justify-between border-t border-border pt-2.5 text-[10.5px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <MapPin size={10} /> {c.city}
        </span>
        <span>{c.members}</span>
      </div>
      <div className="mt-1.5 text-[10px] text-muted-foreground/70">عضو از {c.since}</div>
    </motion.div>
  );
}

function Skel() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-2xl border border-border bg-card p-4">
          <ShimmerBar className="h-14 w-14 rounded-2xl" />
          <ShimmerBar className="h-4 w-2/3" />
          <ShimmerBar className="h-3 w-1/2" />
          <ShimmerBar className="h-3 w-full" />
        </div>
      ))}
    </div>
  );
}

function CustomerCardSkeleton() {
  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
      <ShimmerBar className="h-14 w-14 rounded-2xl" />
      <ShimmerBar className="h-4 w-2/3" />
      <ShimmerBar className="h-3 w-1/2" />
      <ShimmerBar className="h-3 w-full" />
    </div>
  );
}

function CustomersInfinite({ items }: { items: Customer[] }) {
  const { visible, hasMore, loadingMore, sentinelRef, total, visibleCount } =
    useInfiniteList<Customer>({ items, pageSize: 8, loadDelay: 600, resetKey: items });
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {visible.map((c, i) => (
          <CustomerCard key={c.id} c={c} index={i} />
        ))}
        {loadingMore &&
          hasMore &&
          Array.from({ length: 4 }).map((_, k) => <CustomerCardSkeleton key={`sk-${k}`} />)}
      </div>
      <div ref={sentinelRef} className="h-10" aria-hidden />
      <div className="mt-6 flex items-center justify-center">
        {hasMore ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-[11.5px] font-bold text-muted-foreground">
            <Loader2 size={12} className="animate-spin text-primary-main" />
            در حال بارگذاری... ({visibleCount} از {total})
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-[11.5px] font-bold text-muted-foreground">
            <Sparkles size={12} className="text-secondary-main" />
            تمام {total} مشتری نمایش داده شد
          </div>
        )}
      </div>
    </>
  );
}

function Testimonials() {
  return (
    <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-6 sm:pb-24">
      <div className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5">
          <Star size={13} className="text-secondary-main" />
          <span className="text-[11.5px] font-bold text-muted-foreground">از زبان مشتریان</span>
        </div>
        <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">
          آنچه درباره ما می‌گویند
        </h2>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {TESTIMONIALS.map((t, i) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="flex flex-col rounded-2xl border border-border bg-card p-5"
          >
            <Quote size={14} className="mb-2 text-secondary-main" />
            <p className="mb-4 flex-1 text-[12.5px] leading-relaxed text-foreground/90">
              «{t.text}»
            </p>
            <div className="flex items-center gap-3 border-t border-border pt-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br ${t.color} text-[11px] font-black text-white`}
              >
                {t.initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12.5px] font-black text-foreground">{t.author}</div>
                <div className="truncate text-[10.5px] text-muted-foreground">
                  {t.role} · {t.brand}
                </div>
              </div>
              <div className="flex">
                {Array.from({ length: t.rating }).map((_, k) => (
                  <Star key={k} size={11} className="fill-secondary-main text-secondary-main" />
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-5xl px-5 pb-24 sm:px-6">
      <div className="rounded-3xl border border-primary-main/30 bg-gradient-to-br from-primary-main/10 via-card to-secondary-main/10 p-8 text-center sm:p-12">
        <Users size={20} className="mx-auto mb-3 text-primary-main" />
        <h2 className="mb-3 text-2xl font-black text-foreground sm:text-3xl">به جمع ما بپیوند</h2>
        <p className="mx-auto mb-6 max-w-md text-[13px] text-muted-foreground">
          ۱۴ روز رایگان، خدمات فراتر از تصور.
        </p>
        <Link
          href="/login"
          className="inline-flex items-center gap-2 rounded-xl bg-primary-main px-6 py-3 text-[13px] font-black text-white hover:bg-primary-400"
        >
          شروع رایگان <ArrowLeft size={14} />
        </Link>
      </div>
    </section>
  );
}
